import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { lastValueFrom, Subject, zip } from "rxjs";
import { platform } from "../../../platform/platform";
import {
  ConflictImage,
  InspectionImage,
  InspectionImageListItem,
  InspectionImageUpdate,
  ModifiedImageUseUpdate,
  UploadOption
} from "../../_objects/inspection-image";
import { AnnotationImageDetail, AnnotationObjectForUpdate } from "../../_objects/inspection-image-annotation";
import { InspectionReport } from "../../_objects/inspection-report";
import { MarkItem } from "../../_objects/mark";
import { SubjectModel } from "../../_objects/subject-model";
import { Tags } from "../../_objects/tags";
import { TitleItem } from "../../_objects/title";
import { UtilsService } from "../../_services/_utils.service";
import { CommonSnackBarService } from "../../_services/common-snack-bar.service";
import { AnnotationImageService } from "../../_services/http-api/annotation-image.service";
import { InspectionImageService } from "../../_services/http-api/inspection-image.service";
import { InspectionReportService } from "../../_services/http-api/inspection-report.service";
import { TagsService } from "../../_services/http-api/tags.service";
import { LangUpdateService } from "../../_services/lang-update.service";
import { MarksStateService } from "../../_services/mark-state/marks-state.service";
import { MeasurementModeService } from "../../_services/measurement-mode.service";
import { NetworkStatusService } from "../../_services/network-status.service";
import { SpinnerService } from "../../_services/spinner.service";
import { TitlesStateService } from "../../_services/title-state/titles-state.service";
import { EVC_MODAL_DATA, EVC_MODAL_REF, ModalRef } from "../../_shared/services/modal/evc-modal.service";
import { IMAGE_FILETYPE, IMAGE_TYPE, PLATFORM, SCOPE_MODEL, USE_IMAGE_TYPE } from "../../app.constants";
import { CommonDeleteDialogComponent } from "../../common-dialog/common-delete-dialog.component";
import { CommonSaveConfirmDialogComponent } from "../../common-dialog/common-save-confirm-dialog.component";
import { ImageConflictDialogService } from "../../inspection/dialogs/image-conflict-dialog/image-conflict-dialog.service";
import { EvcButtonSize, EvcButtonType, EvcButtonVariant } from "../../shared/enumerations/ui-enumerations";
import { AnnotationEmitData, ImageAnnotationEditComponent } from "./image-annotation-edit/image-annotation-edit.component";
import { ImageMeasurementEditComponent } from "./image-measurement-edit/image-measurement-edit.component";
import { MeasurementMerryComponent } from "./measurement-merry/measurement-merry.component";
import { VideoBookmarksStateService } from "./video-bookmark.state.service";
import { ResponseWifiSettingsPram } from "../../_offline/models/iplex-device/iplex-device";

const IMAGE_EDIT_MODE = {
  DATA: "Data",
  RETOUCH: "Retouch",
  REMEASUREMENT: "Remeasurement",
  PROPERTY: "Property",
  VIDEO_DATA: "VideoData",
  REMEASUREMENT_MERRY: "RemeasurementMerry"
} as const;

type ImageEditMode = (typeof IMAGE_EDIT_MODE)[keyof typeof IMAGE_EDIT_MODE];

interface ImageEditDialogData {
  inspectionImage: InspectionImageListItem;
  inspectionReport: InspectionReport;
  isEditable: boolean;
  index: number;
  images: InspectionImageListItem[];
  subjectModel: SubjectModel;
  isPreviewReport?: boolean;
  applyImage: (img: InspectionImage) => void;
  applyReport: (result: any) => void;
  changeReportLocation: (result: any) => void;
}
@Component({
  selector: "app-image-edit-dialog",
  templateUrl: "./image-edit-dialog.component.html",
  styleUrls: ["./image-edit-dialog.component.scss"],
  standalone: false
})
export class ImageEditDialogComponent implements OnInit, OnDestroy {
  readonly buttonTypes = EvcButtonType;
  readonly buttonSizes = EvcButtonSize;
  readonly buttonVariants = EvcButtonVariant;

  readonly iv7Image = "iv7";
  readonly vegaImage = "vega";
  readonly MARK_LIST = ["", "_A", "_B", "_C", "_D", "_E", "_F", "_G"];

  inspectionImage: InspectionImage = null;
  imageListItem: InspectionImageListItem;
  inspectionReport: InspectionReport;
  subjectModel: SubjectModel;
  videoElementEdit: HTMLVideoElement;
  marks: MarkItem[];
  titles: TitleItem[];
  tags: string[];
  selectLocationId: string;
  selectMarkNum: number;
  title: string;
  tag: string[];
  comment: string;
  originalImagePath: string;
  dataHasError = false;
  imageEditMode: ImageEditMode = IMAGE_EDIT_MODE.DATA;
  merryMeasurementEnabled = false;
  onDestroy$ = new Subject();

  @Output() closeCompleteSnackBar = new EventEmitter();
  @Output() closeEditDialog = new EventEmitter();

  isMoveToOut = false;
  isProcess = false;
  isLoadImage = true;
  isUpdate = false;
  isDisable = false;
  isEditable: boolean;
  index: number;
  imageList: InspectionImageListItem[];
  modifiedImage: Blob = null;
  initDataJson: string;
  annotationObjects: AnnotationImageDetail;
  modifiedAnnotationObjects: any;
  modifiedMeasurementObjects: any;
  isPreviewReport: boolean;
  isUpdateDisplay = false;
  private conflictList: ConflictImage[] = [];
  isVideoContents: boolean;

  @ViewChild(ImageMeasurementEditComponent) remeasurementComponent: ImageMeasurementEditComponent;
  @ViewChild(ImageAnnotationEditComponent) retouchComponent: ImageAnnotationEditComponent;
  @ViewChild(MeasurementMerryComponent) merryMeasurement: MeasurementMerryComponent;

  bookmarkStateChanged: boolean;

  constructor(
    private snackBar: CommonSnackBarService,
    private inspectionImageService: InspectionImageService,
    private inspectionReportService: InspectionReportService,
    private markService: MarksStateService,
    private titleService: TitlesStateService,
    private tagsService: TagsService,
    private utilsService: UtilsService,
    private annotationService: AnnotationImageService,
    private oscSpinnerService: SpinnerService,
    private networkStatusService: NetworkStatusService,
    private measurementModeService: MeasurementModeService,
    private dialog: MatDialog,
    @Inject(EVC_MODAL_REF) private modalRef: ModalRef<any>,
    @Inject(EVC_MODAL_DATA) private data: ImageEditDialogData,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private imageConflictDialogService: ImageConflictDialogService,
    private videoBookmarkStateService: VideoBookmarksStateService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
    this.imageListItem = data.inspectionImage;
    this.inspectionReport = data.inspectionReport;
    this.isEditable = data.isEditable;
    this.index = data.index;
    this.imageList = data.images;
    this.subjectModel = data.subjectModel;
    this.isPreviewReport = data.isPreviewReport ?? false;
  }

  get isOffline() {
    return this.networkStatusService.isOffline;
  }

  ngOnInit(): void {
    this.getMasters();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  private async getOriginalImage() {
    const path = await this.inspectionImageService.getImageBinaryUrl(
      this.inspectionImage.inspectionId,
      this.inspectionImage.id,
      USE_IMAGE_TYPE.ORIGINAL
    );
    const [cleanPath] = path.split("?"); // Remove query parameters for cache busting
    this.originalImagePath = cleanPath;
  }

  getMasters(): void {
    this.isProcess = true;
    this.isLoadImage = true;

    zip(
      this.inspectionImageService.getInspectionImage(this.imageListItem.inspectionId, this.imageListItem.id),
      this.markService.markItems$,
      this.titleService.titleItems$,
      this.tagsService.getTags()
    ).subscribe({
      next: (response) => {
        this.setMasters(response[0], response[1], response[2], response[3]);
        this.getOriginalImage();
      },
      error: (error) => {
        this.snackBar.open(this.translate.instant("imageEditDialog.getMastersError"));
        this.oscSpinnerService.hide(true);
        console.log(error);
      },
      complete: () => {
        this.setInputData(this.inspectionImage);

        if (this.inspectionImage.hasModifiedImage || this.inspectionImage.hasMeasurementImage) {
          this.getImageAnnotation();
        } else {
          this.annotationObjects = null;
          this.modifiedImage = null;
          this.modifiedAnnotationObjects = null;
          this.modifiedMeasurementObjects = null;
          this.resetLoadingState();
        }
      }
    });
  }

  private resetLoadingState() {
    this.oscSpinnerService.hide(true);
    this.isLoadImage = false;
    this.isProcess = false;
  }

  setMasters(inspectionImage: InspectionImage, marks: MarkItem[], titles: TitleItem[], tags: Tags): void {
    this.inspectionImage = inspectionImage;
    this.videoBookmarkStateService.updateInspectionBookmarks(this.inspectionImage.bookMarkOption);
    this.marks = marks;
    this.titles = titles;
    this.tags = tags.items;
    this.setIsVideo(this.inspectionImage);
    this.imageEditMode = this.isVideoContents ? IMAGE_EDIT_MODE.VIDEO_DATA : this.imageEditMode;
    this.merryMeasurementEnabled = this.measurementModeService.isMerryMeasurementSupported(this.inspectionImage.imageType);
  }

  setInputData(inspectionImage: InspectionImage) {
    this.selectLocationId = inspectionImage.subjectLocationId;
    this.selectMarkNum = inspectionImage.metadata.mark;
    this.title = inspectionImage.metadata.title;
    this.tag = inspectionImage.metadata.tags;
    this.comment = inspectionImage.comment;
  }

  getImageAnnotation() {
    this.isLoadImage = true;
    this.isProcess = true;

    this.annotationService.getImageAnnotations(this.inspectionImage.inspectionId, this.inspectionImage.id).subscribe({
      next: (response) => {
        this.annotationObjects = response;
      },
      error: (error) => {
        this.snackBar.open(this.translate.instant("imageEditDialog.getImageAnnotationError"));
        console.log(error);

        this.resetLoadingState();
      },
      complete: () => {
        this.resetLoadingState();
      }
    });
  }

  addBookmark() {
    this.videoElementEdit = document.getElementById("video-playback") as HTMLVideoElement;
    this.videoBookmarkStateService.addBookmark({ mtime: this.videoElementEdit.currentTime.toString() });
  }

  checkNoChange(): boolean {
    if (!this.isEditable) {
      return true;
    }

    return (
      this.selectLocationId === this.inspectionImage.subjectLocationId &&
      this.selectMarkNum === this.inspectionImage.metadata.mark &&
      (this.title === this.inspectionImage.metadata.title || (!this.title && !this.inspectionImage.metadata.title)) &&
      (JSON.stringify(this.tag) === JSON.stringify(this.inspectionImage.metadata.tags) || (!this.tag && !this.inspectionImage.metadata.tags)) &&
      (this.comment === this.inspectionImage.comment || (!this.comment && !this.inspectionImage.comment)) &&
      !this.bookmarkStateChanged
    );
  }

  isDataPlateLocation(subjectLocationId: string): boolean {
    const termLocs = this.utilsService.getLocations(this.subjectModel, null, IMAGE_TYPE.INSPECTIONIMAGE);
    return termLocs.find((loc) => loc.identifier === subjectLocationId)?.isDataPlate ?? false;
  }

  applyMove(isSave: boolean, isBack: boolean) {
    this.submit(isSave, false, true, isBack);
  }

  async apply() {
    switch (this.imageEditMode) {
      case IMAGE_EDIT_MODE.DATA:
        this.submit(false, false);
        break;

      case IMAGE_EDIT_MODE.RETOUCH:
        this.putModifiedImageData(false, IMAGE_EDIT_MODE.RETOUCH, IMAGE_EDIT_MODE.RETOUCH);
        break;

      case IMAGE_EDIT_MODE.REMEASUREMENT:
        this.putModifiedImageData(false, IMAGE_EDIT_MODE.REMEASUREMENT, IMAGE_EDIT_MODE.REMEASUREMENT);
        break;

      case IMAGE_EDIT_MODE.VIDEO_DATA:
        this.submit(false, false, false, undefined, IMAGE_EDIT_MODE.VIDEO_DATA);
        break;

      case IMAGE_EDIT_MODE.REMEASUREMENT_MERRY:
        await this.saveMeasurements();
        this.modalRef.close();
        break;

      default:
        console.warn("Unknown image edit mode:", this.imageEditMode);
    }
  }
  save(isExit?: boolean) {
    this.submit(true, isExit);
  }

  async cancel() {
    if (this.isLoadImage) {
      this.closeEditDialog.emit();
      this.modalRef.close();
    }

    const dialogConf = {
      width: "450px",
      height: "280px",
      disableClose: true,
      data: { isMove: false, isError: this.imageEditMode === IMAGE_EDIT_MODE.REMEASUREMENT && this.isDisable }
    };

    switch (this.imageEditMode) {
      case IMAGE_EDIT_MODE.DATA:
        await this.onCancelData(dialogConf);
        break;
      case IMAGE_EDIT_MODE.VIDEO_DATA:
        await this.onCancelData(dialogConf);
        break;
      case IMAGE_EDIT_MODE.RETOUCH:
        await this.onCancelRetouch(dialogConf);
        break;
      case IMAGE_EDIT_MODE.REMEASUREMENT:
        await this.onCancelRemeasurement(dialogConf);
        break;
      case IMAGE_EDIT_MODE.REMEASUREMENT_MERRY:
        await this.onCancelMerryMeasurement(dialogConf);
        break;
      default:
        this.closeModal(this.isUpdateDisplay);
    }
  }

  private closeModal(isUpdateDisplay?: boolean) {
    this.closeEditDialog.emit();
    this.modalRef.close(isUpdateDisplay);
  }

  async onCancelData(dialogConf: object) {
    if (this.checkNoChange()) {
      this.closeModal(this.isUpdateDisplay);
    } else {
      const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, dialogConf);
      const result = await lastValueFrom(dialogRef.afterClosed());
      if (result) {
        this.save(true);
      } else if (result !== "") {
        this.closeModal();
      }
    }
  }

  async onCancelRetouch(dialogConf: object) {
    if (this.isChangedCanvasRetouchObject()) {
      const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, dialogConf);
      const result = await lastValueFrom(dialogRef.afterClosed());
      if (result) {
        this.putModifiedImageData(true, IMAGE_EDIT_MODE.RETOUCH, IMAGE_EDIT_MODE.RETOUCH);
      } else if (result !== "") {
        this.closeModal();
      }
    } else {
      this.closeModal();
    }
  }

  async onCancelRemeasurement(dialogConf: object) {
    if (!this.isChangedCanvasMeasurementObject()) {
      this.closeModal();
    } else {
      const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, dialogConf);
      const result = await lastValueFrom(dialogRef.afterClosed());
      if (result) {
        this.putModifiedImageData(true, IMAGE_EDIT_MODE.REMEASUREMENT, IMAGE_EDIT_MODE.REMEASUREMENT);
      } else if (result !== "") {
        this.closeModal();
      }
    }
  }

  async onCancelMerryMeasurement(dialogConf: object, mode?: ImageEditMode) {
    const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, dialogConf);
    const result = await lastValueFrom(dialogRef.afterClosed());
    if (result) {
      await this.saveMeasurements();
      this.closeModal();
    } else if (result !== "") {
      this.closeModal();
    }
  }

  submit(isSave: boolean, isExit?: boolean, isMove?: boolean, isBack?: boolean, isModeChange: ImageEditMode = IMAGE_EDIT_MODE.DATA) {
    this.isUpdate = true;
    const req = this.inspectionImageService.convertInspectionImage2InpsctionImageUpdate(this.inspectionImage);
    req.subjectLocationId = this.selectLocationId;
    req.uploadOption = UploadOption.None;
    req.metadata.mark = this.selectMarkNum;
    req.metadata.title = this.title;
    req.metadata.tags = this.tag;
    req.comment = this.comment;
    req.isDataPlate = this.isDataPlateLocation(req.subjectLocationId);
    this.patchInspectionImage(req, isSave, isExit, isMove, isBack, isModeChange);
  }

  patchInspectionImage(
    req: InspectionImageUpdate,
    isSave: boolean,
    isExit?: boolean,
    isMove?: boolean,
    isBack?: boolean,
    isModeChange: ImageEditMode = IMAGE_EDIT_MODE.DATA
  ) {
    let response: any;
    this.oscSpinnerService.show(true);
    this.isProcess = true;

    const saveAndExit = isSave && isExit;
    const exitWithoutSaving = !isSave && isExit && !isMove;

    this.inspectionImageService.patchInspectionImage(req).subscribe({
      next: (data) => {
        response = data;
        this.isUpdateDisplay = true;
      },
      error: (error) => {
        if (error.status === 409) {
          let fileName: string, subjectLocationId: string, isWithoutExtension: boolean;
          if (this.isOffline) {
            fileName = error.body.fileName;
            subjectLocationId = error.body.subjectLocationId;
            isWithoutExtension = error.body.isWithoutExtension;
          } else {
            const message = error.error.message.replace("This inspection image already exists. ", "");
            fileName = message.match(/FileName: {2}(.*), SubjectLocationId/)[1];
            subjectLocationId = message.match(/, SubjectLocationId: {3}(.*)/)[1];
            isWithoutExtension = error.error.message.includes("This inspection data already exists. FileName: ");
          }

          const conflictFile = this.imageList?.find(
            (_) => _.fileName == fileName && (!_.subjectLocationId || _.subjectLocationId == subjectLocationId)
          );
          this.conflictList.push(
            new ConflictImage(
              null,
              conflictFile?.fileName,
              null,
              null,
              this.inspectionImage.thumbFilePath,
              conflictFile?.thumbFilePath,
              isWithoutExtension
            )
          );

          if (!this.imageConflictDialogService.isOpened) {
            this.openConflictDialog(req, isSave, isExit, isMove, isBack, isModeChange);
          }
        } else {
          this.snackBar.open(this.translate.instant("imageEditDialog.submitError"));
          this.oscSpinnerService.hide(true);
          console.log(error);
        }
        this.isProcess = false;
      },
      complete: () => {
        if (isModeChange === IMAGE_EDIT_MODE.DATA || isModeChange === IMAGE_EDIT_MODE.VIDEO_DATA) {
          this.snackBar.open(this.translate.instant("imageEditDialog.submitComplete"));
          this.bookmarkStateChanged = false;
        }
        this.data.inspectionImage = this.inspectionImageService.convertInspectionImage2InspectionImageListItem(this.inspectionImage);
        this.imageListItem = this.data.inspectionImage;

        this.checkSaveAndMove(response, isSave, isMove, isBack);

        this.imageEditMode = isModeChange;
        this.isProcess = false;
        if (isModeChange === IMAGE_EDIT_MODE.DATA) {
          this.oscSpinnerService.hide(true);
        }

        if (exitWithoutSaving || saveAndExit) {
          this.modalRef.close();
        }
      }
    });
  }

  checkSaveAndMove(response: any, isSave: boolean, isMove: boolean, isBack: boolean) {
    if (!isSave) {
      this.inspectionImage = response.inspectionImage;
      this.data.applyReport(response);
      this.modalRef.close();
    }

    if (isMove) {
      this.changeImage(isBack);
    }
  }

  delete() {
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: "600px",
      minWidth: "400px",
      maxHeight: "800px",
      data: { target: this.translate.instant("imageEditDialog.deleteDialogTarget"), deleteNames: [this.inspectionImage.fileName] }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.oscSpinnerService.show(true);
      this.isProcess = true;
      let response: any;

      this.inspectionImageService.deleteInspectionImage(this.inspectionImage.inspectionId, this.inspectionImage.id).subscribe({
        next: (data) => {
          response = data;
        },
        error: (error) => {
          this.snackBar.open(this.translate.instant("imageEditDialog.deleteError"));
          this.oscSpinnerService.hide(true);
          console.log(error);
        },
        complete: () => {
          this.oscSpinnerService.hide(true);
          this.isProcess = false;
          this.snackBar.open(this.translate.instant("imageEditDialog.deleteComplete"));
          response.inspectionImageId = this.inspectionImage.id;
          dialogRef.close(response);
          this.modalRef.close();
        }
      });
    });
  }

  resetMeasurementObject() {
    if (this.remeasurementComponent) {
      this.annotationObjects = null;
      this.remeasurementComponent.resetObject();
    }
  }

  resetImageAnnotation() {
    this.retouchComponent.clearAllModifiedImage();
  }

  clearAllStyle() {
    return this.retouchComponent.getClearAllStyle();
  }

  selectImage4Report(inspectionImage: InspectionImage, post: boolean) {
    if (!this.isSelectedImage4Report()) {
      // add
      if (!this.isSelectedLocation4Report(this.inspectionImage)) {
        this.selectLocation4Report(false);
      }
      this.inspectionReport.reportLocations.forEach((loc) => {
        if (loc.identifier === inspectionImage.subjectLocationId && loc.imageIds) {
          loc.imageIds.push(inspectionImage.id);
        }
      });
    } else {
      // delete
      this.inspectionReport.reportLocations.forEach((location) => {
        if (!location.imageIds) {
          return;
        }

        location.imageIds = location.imageIds.filter((imageId) => imageId !== inspectionImage.id);
      });
    }

    if (post) {
      this.patchLocation4InspectionReport();
    }
  }

  isReportStatusEditing(reportStatus: string): boolean {
    return this.utilsService.isReportStatusEditing(reportStatus) || this.utilsService.isReportStatusFailed(reportStatus);
  }

  patchLocation4InspectionReport() {
    this.isProcess = true;
    const req = this.inspectionReportService.convertInspectionReport2InspectionReportUpdateLocations(this.inspectionReport);
    this.inspectionReportService.patchInspectionReportLocations(req).subscribe({
      next: (data) => {
        this.inspectionReport.reportLocations = data.reportLocations;
      },
      error: (error) => {
        this.isProcess = false;
        console.log(error);
      },
      complete: () => {
        this.isProcess = false;
        this.data.changeReportLocation(this.inspectionReport);
      }
    });
  }

  selectLocation4Report(post: boolean) {
    if (this.inspectionImage.subjectLocationId) {
      return;
    }

    if (!this.isSelectedLocation4Report(this.inspectionImage)) {
      // add
      this.inspectionReport.reportLocations.push({
        identifier: this.inspectionImage.subjectLocationId,
        imageIds: []
      });
    } else {
      // delete
      this.inspectionReport.reportLocations = this.inspectionReport.reportLocations.filter(
        (repLoc) => repLoc.identifier !== this.inspectionImage.subjectLocationId
      );
    }

    if (post) {
      this.patchLocation4InspectionReport();
    }
  }

  isShowSelectedIcon(): boolean {
    if (this.inspectionReport && this.inspectionImage && this.inspectionImage.subjectLocationId) {
      return true;
    }
    return false;
  }

  havePermissionOnReport(): boolean {
    if (this.inspectionReport) {
      return !(!this.isReportStatusEditing(this.inspectionReport.status) || !this.isEditable);
    }
    return false;
  }

  isSelectedImage4Report(): boolean {
    return this.inspectionReport?.reportLocations.some((loc) => loc.imageIds?.some((id) => id === this.inspectionImage?.id));
  }

  isSelectedLocation4Report(inspectionImage: InspectionImage): boolean {
    return this.inspectionReport.reportLocations.some((loc) => loc.identifier === inspectionImage.subjectLocationId);
  }

  indexChange(isBack: boolean): void {
    if (this.isLoadImage) {
      return;
    }

    if (this.checkNoChange()) {
      this.changeImage(isBack);
    } else {
      this.openSaveConfirmDialog(isBack);
    }
  }

  openSaveConfirmDialog(isBack: boolean) {
    const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, {
      width: "450px",
      height: "280px",
      disableClose: true,
      data: { isMove: true, isError: false }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.applyMove(true, isBack);
      } else if (result !== "") {
        this.changeImage(isBack);
      }
    });
  }

  changeImage(isBack: boolean) {
    if (isBack) {
      this.getImage(this.index - 1, false, false, IMAGE_EDIT_MODE.DATA);
    } else {
      // forward
      if (this.isMoveToOut) {
        this.getImage(this.index, false, false, IMAGE_EDIT_MODE.DATA);
        this.isMoveToOut = false;
      } else {
        this.getImage(this.index + 1, false, false, IMAGE_EDIT_MODE.DATA);
      }
    }
  }

  getImage(index: number, isEmit = false, isClose = false, transition: ImageEditMode) {
    if (0 <= index && index < this.imageList.length) {
      this.isLoadImage = true;
      this.oscSpinnerService.show(true);
      this.isProcess = true;

      this.inspectionImageService.getInspectionImage(this.imageList[index].inspectionId, this.imageList[index].id).subscribe({
        next: (response) => {
          this.inspectionImage = response;
        },
        error: (error) => {
          this.snackBar.open(this.translate.instant("imageEditDialog.getImageError"));
          console.log(error);
          this.resetLoadingState();
        },
        complete: async () => {
          this.setInputData(this.inspectionImage);
          this.setIsVideo(this.inspectionImage);
          this.imageEditMode = this.isVideoContents ? IMAGE_EDIT_MODE.VIDEO_DATA : transition;
          this.index = index;
          this.videoBookmarkStateService.updateInspectionBookmarks(this.inspectionImage.bookMarkOption);
          this.bookmarkStateChanged = false;
          await this.getOriginalImage();

          if (isEmit) {
            this.data.applyImage(this.inspectionImage);
          }

          if (isClose) {
            this.closeModal();
          } else if (this.inspectionImage.hasModifiedImage || this.inspectionImage.hasMeasurementImage) {
            this.getImageAnnotation();
          } else {
            this.annotationObjects = null;
            this.modifiedImage = null;
            this.modifiedAnnotationObjects = null;
            this.modifiedMeasurementObjects = null;

            this.resetLoadingState();
          }
        }
      });
    }
  }

  changeMode(mode: ImageEditMode) {
    const dialogConf = {
      width: "450px",
      height: "280px",
      disableClose: true,
      data: { isMove: false, isError: this.imageEditMode === IMAGE_EDIT_MODE.REMEASUREMENT && this.isDisable }
    };

    if (this.imageEditMode === IMAGE_EDIT_MODE.RETOUCH) {
      // Retouch =>
      if (this.isChangedCanvasRetouchObject()) {
        // Retouchの変更確認
        const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, dialogConf);
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.putModifiedImageData(false, this.imageEditMode, mode);
          } else if (result !== "") {
            this.resetRetouchData();
            this.imageEditMode = mode;
          }
        });
      } else {
        this.imageEditMode = mode;
      }
    } else if (this.imageEditMode === IMAGE_EDIT_MODE.DATA || this.imageEditMode === IMAGE_EDIT_MODE.VIDEO_DATA) {
      // Data =>
      if (!this.checkNoChange()) {
        // dataの変更確認
        const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, dialogConf);
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.submit(true, false, false, false, mode);
          } else if (result !== "") {
            this.resetDataData();
            this.imageEditMode = mode;
          }
        });
      } else {
        this.imageEditMode = mode;
      }
    } else if (this.imageEditMode === IMAGE_EDIT_MODE.REMEASUREMENT) {
      // Measurement =>
      if (this.isChangedCanvasMeasurementObject()) {
        // Confirm measurement changes
        const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, dialogConf);
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.putModifiedImageData(false, this.imageEditMode, mode);
          } else if (result !== "") {
            this.resetRemeasurementData();
            this.imageEditMode = mode;
          }
        });
      } else {
        this.imageEditMode = mode;
      }
    } else if (this.imageEditMode === IMAGE_EDIT_MODE.PROPERTY) {
      // Property =>
      this.imageEditMode = mode;
    } else if (this.imageEditMode === IMAGE_EDIT_MODE.REMEASUREMENT_MERRY) {
      const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, dialogConf);
      dialogRef.afterClosed().subscribe(async (result) => {
        if (result) {
          await this.saveMeasurements();
          this.imageEditMode = mode;
        } else if (result !== "") {
          this.imageEditMode = mode;
        }
      });
    } else {
      this.imageEditMode = mode;
    }
  }

  // Dataタブでの変更データをリセット
  resetDataData() {
    this.selectLocationId = this.inspectionImage.subjectLocationId;
    this.selectMarkNum = this.inspectionImage.metadata.mark;
    this.title = this.inspectionImage.metadata.title;
    this.tag = this.inspectionImage.metadata.tags;
    this.comment = this.inspectionImage.comment;
  }

  // Retouchタブでの変更データをリセット
  resetRetouchData() {
    this.modifiedAnnotationObjects = this.annotationObjects?.modifiedItems;
  }

  // Remeasurementタブでの変更データをリセット
  resetRemeasurementData() {
    this.modifiedMeasurementObjects = this.annotationObjects?.measurementItems;
    this.isDisable = false;
  }

  modified(modifiedData: AnnotationEmitData) {
    this.initDataJson = modifiedData.initDataJson;
    this.modifiedImage = modifiedData.modifiedImage;
    this.modifiedAnnotationObjects = modifiedData.annotationObjects;
    this.modifiedMeasurementObjects = modifiedData.measurementObjects;
    this.isDisable = modifiedData.isDisable;
  }

  putModifiedImageData(isClose: boolean, editMode: ImageEditMode, transition: ImageEditMode): void {
    this.isLoadImage = true;
    this.isProcess = true;
    this.oscSpinnerService.show(true);
    let useMode;
    if (editMode === IMAGE_EDIT_MODE.DATA) {
      useMode = 0;
    } else if (editMode === IMAGE_EDIT_MODE.RETOUCH) {
      useMode = 1;
    } else if (editMode === IMAGE_EDIT_MODE.REMEASUREMENT) {
      useMode = 2;
    }

    const annotationObj = this.isChangedInitData(editMode) ? this.modifiedAnnotationObjects : null;
    const measureObj = this.isChangedInitData(editMode) ? this.modifiedMeasurementObjects : null;

    const annotationReq = new AnnotationObjectForUpdate(this.inspectionImage.id, annotationObj, measureObj, this.inspectionImage.schemaVersion);
    this.annotationService.putImageAnnotations(annotationReq, this.inspectionImage.inspectionId, useMode).subscribe({
      next: (response) => {
        this.annotationObjects = response;
      },
      error: (error) => {
        this.isOffline
          ? this.snackBar.open(this.translate.instant("imageEditDialog.saveModifiedImageDataError"))
          : this.snackBar.open(this.translate.instant("imageEditDialog.uploadModifiedImageDataError"));
        console.log(error);
        this.resetLoadingState();
      },
      complete: () => {
        if (
          (editMode === IMAGE_EDIT_MODE.RETOUCH && this.annotationObjects.modifiedItems !== null) ||
          (editMode === IMAGE_EDIT_MODE.REMEASUREMENT && this.annotationObjects.measurementItems !== null)
        ) {
          this.putModifiedImage(isClose, editMode, transition);
        } else {
          this.getImage(this.index, true, isClose, transition);
        }
      }
    });
  }

  putModifiedImage(isClose: boolean, editMode: ImageEditMode, transition: ImageEditMode): void {
    this.isLoadImage = true;
    this.isProcess = true;
    const file = new File([this.modifiedImage], this.inspectionImage.fileName, { type: "image/jpeg" });

    let useMode;
    if (editMode === IMAGE_EDIT_MODE.DATA) {
      useMode = 0;
    } else if (editMode === IMAGE_EDIT_MODE.RETOUCH) {
      useMode = 1;
    } else if (editMode === IMAGE_EDIT_MODE.REMEASUREMENT) {
      useMode = 2;
    }

    this.annotationService.putModifiedImage(this.inspectionImage.inspectionId, this.inspectionImage.id, file, useMode).subscribe({
      next: (response) => {
        this.annotationObjects = response;
      },
      error: (error) => {
        this.isOffline
          ? this.snackBar.open(this.translate.instant("imageEditDialog.saveModifiedImageError"))
          : this.snackBar.open(this.translate.instant("imageEditDialog.uploadModifiedImageError"));
        console.log(error);
        this.resetLoadingState();
      },
      complete: () => {
        this.getImage(this.index, true, isClose, transition);
        this.imageEditMode = transition;
      }
    });
  }

  public toggleUseModifiedImage(usedImage): void {
    this.oscSpinnerService.show(true);
    this.isProcess = true;
    const req = new ModifiedImageUseUpdate();
    req.id = this.inspectionImage.id;
    req.inspectionId = this.inspectionImage.inspectionId;
    req.usedImageMode = usedImage;

    this.inspectionImageService.putInspImageModifiedUse(req).subscribe({
      next: (response) => {
        this.inspectionImage = response;
      },
      error: (error) => {
        console.log(error);
        this.snackBar.open(this.translate.instant("imageEditDialog.toggleUseModifiedImageError"));
        this.inspectionImage.usedImageMode = usedImage;
        this.oscSpinnerService.hide(true);
        this.isProcess = false;
      },
      complete: () => {
        this.data.applyImage(this.inspectionImage);
        this.oscSpinnerService.hide(true);
        this.isProcess = false;
      }
    });
  }

  isChangedInitData(editMode: string): boolean {
    let srcCopied: any = null;
    srcCopied = JSON.parse(JSON.stringify(this.initDataJson)).objects;
    srcCopied[0].src = null;

    let destCopied: any = null;
    if (editMode === IMAGE_EDIT_MODE.RETOUCH && this.modifiedAnnotationObjects && this.modifiedAnnotationObjects.length > 0) {
      destCopied = JSON.parse(JSON.stringify(this.modifiedAnnotationObjects));
      destCopied[0].src = null;

      // ClearAllで保存された場合
      if (this.isUneditedRetouchImage(this.modifiedAnnotationObjects)) {
        return false;
      }
    } else if (editMode === IMAGE_EDIT_MODE.REMEASUREMENT && this.modifiedMeasurementObjects && this.modifiedMeasurementObjects.length > 0) {
      destCopied = JSON.parse(JSON.stringify(this.modifiedMeasurementObjects));
      destCopied[0].src = null;

      // 計測件数0で保存した場合
      if (this.modifiedMeasurementObjects.length === 1) {
        return true;
      }
    }

    return JSON.stringify(srcCopied) !== JSON.stringify(destCopied);
  }

  isUneditedRetouchImage(annotationObjects: any): boolean {
    if (!annotationObjects) {
      return true;
    }

    return (
      annotationObjects.length === 1 &&
      annotationObjects[0].type === "image" &&
      annotationObjects[0].filters[0].brightness === 0 &&
      annotationObjects[0].angle === 0 &&
      annotationObjects[0].width === this.inspectionImage.originalWidth &&
      annotationObjects[0].height === this.inspectionImage.originalHeight
    );
  }

  onBookmarksChanged(changed: boolean): void {
    this.videoBookmarkStateService.inspectionBookmarks.subscribe((bookmarks) => (this.inspectionImage.bookMarkOption = bookmarks));
    this.bookmarkStateChanged = changed;
  }

  isChangedCanvasRetouchObject(): boolean {
    if (!this.isEditable || !this.initDataJson) {
      return false;
    }

    let srcCopied: any = null;
    if (this.annotationObjects && this.annotationObjects.modifiedItems && this.annotationObjects.modifiedItems.length > 0) {
      srcCopied = JSON.parse(JSON.stringify(this.annotationObjects.modifiedItems));
      srcCopied[0].src = null;
      // dataプロパティの挿入位置の違いで等しくないと判断される為、dataプロパティを削除してCheck
      delete srcCopied[0].data;
    } else {
      srcCopied = JSON.parse(JSON.stringify(this.initDataJson)).objects;
      srcCopied[0].src = null;
      delete srcCopied[0].data;
    }

    let destCopied: any = null;
    if (this.modifiedAnnotationObjects && this.modifiedAnnotationObjects.length > 0) {
      destCopied = JSON.parse(JSON.stringify(this.modifiedAnnotationObjects));
      destCopied[0].src = null;
      delete destCopied[0].data;
    }
    return destCopied && JSON.stringify(srcCopied) !== JSON.stringify(destCopied);
  }

  isChangedCanvasMeasurementObject(): boolean {
    if (!this.isEditable) {
      return false;
    }

    let srcCopied: any = null;
    if (this.annotationObjects && this.annotationObjects.measurementItems && this.annotationObjects.measurementItems.length > 1) {
      srcCopied = JSON.parse(JSON.stringify(this.annotationObjects.measurementItems));
      srcCopied[0] = null;
    }

    let destCopied: any = null;
    if (this.modifiedMeasurementObjects && this.modifiedMeasurementObjects.length > 1) {
      destCopied = JSON.parse(JSON.stringify(this.modifiedMeasurementObjects));
      destCopied[0] = null;
    }

    return JSON.stringify(srcCopied) !== JSON.stringify(destCopied);
  }

  get isImageMeasurable(): boolean {
    return this.imageListItem.isMeasurementEnabled;
  }

  /** Cloud re-measurement of IPLEX One images is not available yet, so the tab stays inactive on web (VISOL-3510). */
  get isRemeasurementRestrictedOnWeb(): boolean {
    return platform.name === PLATFORM.WEB && this.inspectionImage?.scopeModel === SCOPE_MODEL.IPLEX_ONE;
  }

  private openConflictDialog(
    req: InspectionImageUpdate,
    isSave: boolean,
    isExit?: boolean,
    isMove?: boolean,
    isBack?: boolean,
    changedMode: ImageEditMode = IMAGE_EDIT_MODE.DATA
  ): void {
    this.oscSpinnerService.hide(true);
    const dialogRef = this.imageConflictDialogService.open(this.conflictList, { applyToAllEnabled: false, cancelEnabled: true });
    dialogRef.componentInstance.dialogAction.subscribe((conflictAction) => {
      this.imageConflictDialogService.nextConflictImage();

      if (conflictAction.uploadOption !== UploadOption.Skip) {
        req.uploadOption = conflictAction.uploadOption;
        this.patchInspectionImage(req, isSave, isExit, isMove, isBack, changedMode);
      }
    });

    dialogRef.componentInstance.cancelAction.subscribe(() => {
      this.imageConflictDialogService.close();
    });
  }

  setIsVideo(image: InspectionImage) {
    const result: string = this.utilsService.getMimeType(image.fileName);
    this.isVideoContents = result === IMAGE_FILETYPE.MP4;
  }

  private async saveMeasurements() {
    await this.merryMeasurement.save();
  }

  get isApplyDisabled() {
    let hasBeenModified = true;
    switch (this.imageEditMode) {
      case IMAGE_EDIT_MODE.REMEASUREMENT:
        hasBeenModified = this.isChangedCanvasMeasurementObject();
        break;
      case IMAGE_EDIT_MODE.RETOUCH:
        hasBeenModified = this.isChangedCanvasRetouchObject();
        break;
      case IMAGE_EDIT_MODE.VIDEO_DATA:
        return !this.bookmarkStateChanged;
    }

    return this.dataHasError || this.isProcess || !this.isEditable || this.isLoadImage || this.isDisable || !hasBeenModified;
  }
}
